/**
 * Container Management Constraints
 *
 * Configurable constants for container layout rules.
 * Centralized here for easy modification and future extensibility.
 */

/**
 * Container layout constraints
 */
export const CONTAINER_CONSTRAINTS = {
  /** Maximum number of containers allowed in a single row */
  MAX_CONTAINERS_PER_ROW: 3,
  /** Maximum nesting depth (0 = root, 1 = nested, 2 = nested-nested) */
  MAX_NESTING_DEPTH: 3,
  /** Minimum width percentage for any container */
  MIN_WIDTH_PERCENT: 25,
  /** Minimum width in pixels before containers stack vertically */
  MIN_CONTAINER_WIDTH_PX: 180,
  /** Maximum width percentage for any container */
  MAX_WIDTH_PERCENT: 100,
  /** Default row index for new containers */
  DEFAULT_ROW_INDEX: 0,
} as const;

/**
 * Type for container constraints - allows for runtime customization
 */
export type ContainerConstraints = typeof CONTAINER_CONSTRAINTS;

/**
 * Width distribution presets for common scenarios
 */
export const WIDTH_PRESETS = {
  /** Single container in row */
  SINGLE: '100%',
  /** Two equal containers in row */
  HALF: '50%',
  /** Three equal containers in row */
  THIRD: '33%',
  /** Two containers: 2/3 and 1/3 */
  TWO_THIRDS: '66%',
  /** Two containers: 1/3 and 2/3 */
  ONE_THIRD: '34%',
} as const;

/**
 * Default container configuration values
 */
export const DEFAULT_CONTAINER_VALUES = {
  HEIGHT: 'auto',
  ALIGNMENT: 'left' as const,
  LAYOUT: 'vertical' as const,
  SHOW_TITLE: true,
  WIDTH_LOCKED: false,
  BEHAVE_AS_FILTER: false,
  COLLAPSIBLE: false,
} as const;
